/**
 * Iris Recommendations — Fin-style improvement suggestions mined from
 * unresolved conversations. Iris clusters the conversations it couldn't
 * resolve and produces ranked cards, each tagged with the kind of gap that
 * blocked it:
 *   - CONTENT_GAP — missing knowledge / help content
 *   - DATA_GAP    — missing customer/account data the agent needed
 *   - ACTION_GAP  — a tool/automation the agent lacked to complete the task
 * Operators triage each card through a lifecycle (Suggested → Accepted /
 * Rejected → Done).
 *
 * Reached through the operations proxy at `/api/messaging/iris/recommendations`
 * (operations is the FE's auth boundary + RBAC gate; it forwards to
 * ai-operations with the shared service key), mirroring the sibling
 * `review.ts` reviews hooks.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/recommendations';

/** The kind of gap a recommendation addresses. */
export type GapType = 'CONTENT_GAP' | 'DATA_GAP' | 'ACTION_GAP';

/** Triage lifecycle of a recommendation. */
export type SuggestionStatus =
  /** Freshly generated, not yet triaged. */
  | 'OPEN'
  /** Operator agreed it's worth doing. */
  | 'ACCEPTED'
  /** Operator dismissed it. */
  | 'REJECTED'
  /** The gap has been closed. */
  | 'DONE';

/** One ranked improvement card. */
export interface IrisRecommendation {
  id: string;
  gapType: GapType;
  status: SuggestionStatus;
  title: string;
  description: string;
  /** How many unresolved conversations this gap affected — drives ranking. */
  impactCount: number;
  /** Representative topics/questions this gap showed up in. */
  exampleTopics: string[];
  createdAt: string;
  updatedAt: string;
}

export interface IrisRecommendationList {
  items: IrisRecommendation[];
}

const recommendationsRoot = ['messaging', 'iris-recommendations'] as const;
export const irisRecommendationsKey = (status?: SuggestionStatus) =>
  [...recommendationsRoot, 'list', status ?? 'ALL'] as const;

/** List recommendations, optionally filtered by triage status. `status`
 * omitted = all. */
export function useRecommendations(status?: SuggestionStatus) {
  return useQuery({
    queryKey: irisRecommendationsKey(status),
    queryFn: () => {
      const qs = status ? `?status=${status}` : '';
      return apiGet<IrisRecommendationList>(operationsClient, `${BASE}${qs}`);
    },
    staleTime: 15_000,
  });
}

/** Mine recent unresolved conversations and (re)generate ranked
 * recommendation cards. */
export function useGenerateRecommendations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit?: number) =>
      apiPost<{ created: number }, { limit?: number }>(
        operationsClient,
        `${BASE}/generate`,
        limit ? { limit } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: recommendationsRoot });
    },
  });
}

/** Move a recommendation through its triage lifecycle (Accept / Reject / Mark
 * done). */
export function useSetSuggestionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: SuggestionStatus }) =>
      apiPut<IrisRecommendation, { status: SuggestionStatus }>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}/status`,
        { status },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: recommendationsRoot });
    },
  });
}
